<div class="v2-page">
  <div class="v2-head">
    <h2>Billing</h2>
    <span class="v2-sub">Settlement reconciliation &amp; invoices — all customers</span>
  </div>

  <div class="v2-tab-body">
    <!-- Applies to every tab below: month + the two invoice-producing actions. -->
    <div class="v2-toolbar v2-toolbar-main">
      <mat-form-field appearance="outline" class="v2-month-select">
        <mat-label>Month</mat-label>
        <mat-select [(ngModel)]="invoiceMonth" (selectionChange)="onInvoiceMonthChange()">
          <mat-option *ngFor="let m of monthOptions" [value]="m.value">{{ m.label }}</mat-option>
        </mat-select>
      </mat-form-field>
      <button mat-flat-button color="primary" (click)="printInvoices()" [disabled]="generatingInvoices"
              matTooltip="Confirmed subsidy/credit (per group) + approved møtemat + any garantiomsetning shortfall for this month">
        <mat-icon>receipt_long</mat-icon> {{ generatingInvoices ? 'Generating…' : 'Generate invoices' }}
      </button>
      <mat-spinner *ngIf="generatingInvoices" diameter="24" class="v2-inline-spin"></mat-spinner>
      <button mat-stroked-button (click)="exportInvoicesExcel()" [disabled]="exportingInvoices"
              matTooltip="Same invoices as 'Generate invoices', as an Excel file: one sheet per invoice, per line and per line type">
        <mat-icon>table_view</mat-icon> {{ exportingInvoices ? 'Exporting…' : 'Export to Excel' }}
      </button>
      <mat-spinner *ngIf="exportingInvoices" diameter="24" class="v2-inline-spin"></mat-spinner>
      <button mat-stroked-button (click)="downloadInvoiceSpecs()" [disabled]="exportingSpecs"
              matTooltip="One Excel file per invoice listing every transaction behind its subsidy, credit and pant lines — the attachment for the customer. Downloads as a zip.">
        <mat-icon>list_alt</mat-icon> {{ exportingSpecs ? 'Building…' : 'Transaction specs' }}
      </button>
      <mat-spinner *ngIf="exportingSpecs" diameter="24" class="v2-inline-spin"></mat-spinner>
      <button mat-stroked-button (click)="checkPowerOffice()" [disabled]="checkingPowerOffice || sendingToPowerOffice"
              matTooltip="Checks every invoice against PowerOffice Go first (read-only): customer, VAT per line, products, invoices already sent. You then choose which to create as DRAFTS. Nothing is emailed to customers.">
        <mat-icon>fact_check</mat-icon> {{ checkingPowerOffice ? 'Checking…' : (sendingToPowerOffice ? 'Sending…' : 'Check & send to PowerOffice') }}
      </button>
      <mat-spinner *ngIf="checkingPowerOffice || sendingToPowerOffice" diameter="24" class="v2-inline-spin"></mat-spinner>
      <button mat-stroked-button (click)="exportPowerOfficeImportFile()" [disabled]="exportingPowerOfficeFile"
              matTooltip="The same draft invoices as 'Send to PowerOffice', as PowerOffice Go's standard [Orders] import file — upload it by hand under Regnskap → Import → Standardimport. Attach the transaction specs yourself; importing a file twice creates the drafts twice.">
        <mat-icon>download</mat-icon> {{ exportingPowerOfficeFile ? 'Building…' : 'PowerOffice import file' }}
      </button>
      <mat-spinner *ngIf="exportingPowerOfficeFile" diameter="24" class="v2-inline-spin"></mat-spinner>
      <span class="v2-spacer"></span>
      <label class="v2-toggle" *ngIf="_isSuperAdmin"
             matTooltip="Fan reconciliation out across every company and merge the results">
        <input type="checkbox" [(ngModel)]="showAllCompanies"> All companies
      </label>
    </div>

    <!-- Invoice-action errors raised from the toolbar, visible whatever tab is open
         (the Reconcile tab shows the same message in place). -->
    <div class="v2-empty v2-error" *ngIf="reconError && selectedTabIndex !== 0"><mat-icon>error_outline</mat-icon> {{ reconError }}</div>
    <div class="v2-empty v2-error" *ngIf="powerOfficeError"><mat-icon>error_outline</mat-icon> {{ powerOfficeError }}</div>
    <div class="v2-empty v2-error" *ngIf="notActiveNote"><mat-icon>block</mat-icon> {{ notActiveNote }}</div>
    <div class="v2-empty" *ngIf="powerOfficeFileNote"><mat-icon>download_done</mat-icon> {{ powerOfficeFileNote }}</div>
    <!-- PowerOffice check: what each invoice would become, before anything is created. -->
    <div class="po-check" *ngIf="poCheck" [class.po-prod]="poCheck.environment === 'production'">
      <div class="po-check-head">
        <span class="po-env">{{ poCheck.environment === 'production' ? 'PRODUCTION' : 'DEMO' }}</span>
        <span>PowerOffice client: <strong>{{ poCheck.client?.name }}</strong></span>
        <span>Month: <strong>{{ poCheck.month }}</strong></span>
        <span class="v2-chip v2-chip-warn" *ngIf="poCheck.client?.allowSendInvoice"
              matTooltip="The integration has PowerOffice's invoice-sending privilege. This page never uses it, but ask PowerOffice to remove it.">Integration may send invoices</span>
        <span class="v2-spacer"></span>
        <span>{{ poCheck.rows.length - poBlockedCount }} ready · {{ poBlockedCount }} blocked</span>
      </div>
      <div class="v2-table-wrap">
        <table class="v2-plain-table">
          <thead><tr><th *ngIf="poCheck.environment !== 'production'"></th><th>Customer</th><th>PowerOffice customer</th><th>Reference</th><th class="num">Total incl. VAT</th><th>Check</th><th></th></tr></thead>
          <tbody>
            <tr *ngFor="let r of poCheck.rows" [class.po-blocked]="!r.ok">
              <td *ngIf="poCheck.environment !== 'production'"><input type="checkbox" [(ngModel)]="r.selected" [disabled]="!r.ok || r.sent === 'ok'"></td>
              <td>{{ r.customerName }}</td>
              <td>{{ r.customer ? ((r.customer.number || 'new') + ' ' + r.customer.name + (r.customer.isVatFree ? ' (VAT-free)' : '')) : '—' }}</td>
              <td>{{ r.ref }}</td>
              <td class="num">{{ r.ourTotal | number:'1.2-2' }}</td>
              <td>
                <span class="v2-chip v2-chip-ok" *ngIf="r.ok && !r.warnings?.length">OK</span>
                <div class="po-msg po-err" *ngFor="let e of r.errors">{{ e }}</div>
                <div class="po-msg" *ngFor="let w of r.warnings">{{ w }}</div>
              </td>
              <td>
                <span class="v2-chip v2-chip-ok" *ngIf="r.sent === 'ok'">Sent</span>
                <button mat-stroked-button *ngIf="r.ok && r.sent !== 'ok'" [color]="poCheck.environment === 'production' ? 'warn' : 'primary'"
                        (click)="sendCustomerToPowerOffice(r)" [disabled]="sendingToPowerOffice"
                        matTooltip="Create this customer's draft invoice(s) only">
                  {{ r.sent === 'failed' ? 'Retry' : 'Send' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="po-check-foot">
        <button mat-flat-button color="primary" *ngIf="poCheck.environment !== 'production'"
                (click)="sendCheckedToPowerOffice()" [disabled]="sendingToPowerOffice || !poSelected.length">
          <mat-icon>cloud_upload</mat-icon>
          Create {{ poSelected.length }} draft(s) in {{ poCheck.environment === 'production' ? 'PRODUCTION' : 'demo' }} · {{ poSelectedTotal | number:'1.2-2' }} kr
        </button>
        <button mat-flat-button [color]="poCheck.environment === 'production' ? 'warn' : 'primary'"
                (click)="sendAllToPowerOffice()" [disabled]="sendingToPowerOffice || !poReady.length"
                matTooltip="Create draft invoices for every ready customer that is not sent yet. Blocked invoices are skipped.">
          <mat-icon>cloud_upload</mat-icon>
          Send all {{ poReady.length }} to {{ poCheck.environment === 'production' ? 'PRODUCTION' : 'demo' }} · {{ poReadyTotal | number:'1.2-2' }} kr
        </button>
        <span *ngIf="poSendProgress">Sending customer {{ poSendProgress }}…</span>
        <button mat-stroked-button (click)="poCheck = null" [disabled]="sendingToPowerOffice">Close</button>
        <span class="po-hint">{{ poCheck.environment === 'production' ? 'Production: Send all creates the drafts one customer at a time. ' : '' }}Blocked invoices cannot be sent. Fix them and run the check again.</span>
      </div>
    </div>

    <div class="v2-table-wrap" style="margin-bottom: 14px;" *ngIf="powerOfficeResults?.length">
      <table class="v2-plain-table">
        <thead><tr><th>Customer</th><th>Status</th><th>Specification</th><th>PowerOffice sales order</th></tr></thead>
        <tbody>
          <tr *ngFor="let r of powerOfficeResults">
            <td>{{ r.customerName }}</td>
            <td>
              <span class="v2-chip v2-chip-ok" *ngIf="r.ok">Draft created</span>
              <span class="v2-chip v2-chip-warn" *ngIf="!r.ok" [matTooltip]="r.error">Failed</span>
            </td>
            <td>
              <span class="v2-chip v2-chip-ok" *ngIf="r.spec === 'attached'">Attached</span>
              <span *ngIf="r.spec === 'pending'">Uploading…</span>
              <span class="v2-chip v2-chip-warn" *ngIf="r.spec === 'failed'" [matTooltip]="r.specError">Not attached</span>
              <span *ngIf="r.spec === 'none'">—</span>
            </td>
            <td>{{ r.powerOfficeSalesOrderId || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <mat-tab-group class="v2-tabs" animationDuration="150ms" [(selectedIndex)]="selectedTabIndex" [preserveContent]="true">

      <!-- Reconcile & Invoice -------------------------------------------- -->
      <mat-tab>
        <ng-template mat-tab-label>
          <span class="v2-tab-label"
                [matBadge]="reconResult?.unassignedBreakdown?.length" matBadgeSize="small" matBadgeOverlap="false"
                [matBadgeHidden]="!reconResult?.unassignedBreakdown?.length">
            <mat-icon>fact_check</mat-icon> Reconcile &amp; Invoice
          </span>
        </ng-template>

        <div class="v2-tab-panel">
          <p class="v2-note">
            Upload the month's settlement exports. Each card transaction is matched to a settlement by
            order&nbsp;ID; only captured (non-refunded) subsidy is billed. Credit spend is confirmed internally.
          </p>

          <div class="v2-toolbar v2-recon-toolbar">
            <label class="v2-file-btn" [class.v2-file-set]="vippsFileName" mat-stroked-button>
              <mat-icon>{{ vippsFileName ? 'check_circle' : 'upload_file' }}</mat-icon>
              {{ vippsFileName || 'Vipps settlement (.csv)' }}
              <input type="file" accept=".csv,.xlsx,.xls" (change)="onVippsFile($event)" hidden>
            </label>
            <label class="v2-file-btn" [class.v2-file-set]="stripeFileName" mat-stroked-button>
              <mat-icon>{{ stripeFileName ? 'check_circle' : 'upload_file' }}</mat-icon>
              {{ stripeFileName || 'Stripe export (.csv)' }}
              <input type="file" accept=".csv,.xlsx,.xls" (change)="onStripeFile($event)" hidden>
            </label>
            <button mat-flat-button color="primary" (click)="runReconcile()" [disabled]="reconLoading">
              <mat-icon>fact_check</mat-icon> Run reconciliation
            </button>
            <mat-spinner *ngIf="reconLoading" diameter="24" class="v2-inline-spin"></mat-spinner>
          </div>

          <div class="v2-empty v2-error" *ngIf="reconError"><mat-icon>error_outline</mat-icon> {{ reconError }}</div>

          <ng-container *ngIf="reconResult && reconResult.success !== false">
            <!-- summary chips -->
            <div class="v2-recon-summary">
              <span class="v2-chip">Transactions: {{ reconResult.summary.transactions }}</span>
              <span class="v2-chip v2-chip-ok">Confirmed: {{ reconResult.summary.confirmed }}</span>
              <span class="v2-chip">Credit: {{ reconResult.summary.credit }}</span>
              <span class="v2-chip" *ngIf="reconResult.summary.gavekort"
                    matTooltip="Purchases paid in full by gavekort are compensations: not billed to any customer">Gavekort (not billed): {{ reconResult.summary.gavekort }}</span>
              <span class="v2-chip v2-chip-warn">Unmatched: {{ reconResult.summary.unmatched }}</span>
              <span class="v2-chip v2-chip-warn">Refunded/void: {{ reconResult.summary.refunded }}</span>
              <span class="v2-chip" *ngIf="reconResult.summary.partialRefund">Partial refunds: {{ reconResult.summary.partialRefund }}</span>
              <span class="v2-chip" *ngIf="reconResult.summary.invoiceBySystemMoved"
                    matTooltip="Purchases at 'Invoice by system' systems billed to the system's customer instead of the buyer's group's">Invoiced by system: {{ reconResult.summary.invoiceBySystemMoved }}</span>
              <span class="v2-chip v2-chip-warn" *ngIf="reconResult.summary.invoiceBySystemAmbiguous"
                    matTooltip="These systems have 'Invoice by system' on but are linked to no customer or to several — link each to exactly one customer (Kundekort → Systems) and re-run">Invoice by system — unclear customer: {{ reconResult.summary.invoiceBySystemAmbiguous }} tx</span>
            </div>

            <!-- diagnostics: what each file covered vs what's unmatched -->
            <div class="v2-recon-summary v2-recon-diag">
              <span class="v2-chip">Vipps file: {{ reconResult.summary.settlementVipps }} orders</span>
              <span class="v2-chip">Stripe file: {{ reconResult.summary.settlementStripe }} orders</span>
              <span class="v2-chip"
                    [class.v2-chip-warn]="reconResult.summary.unmatchedVipps">Unmatched Vipps tx: {{ reconResult.summary.unmatchedVipps }}</span>
              <span class="v2-chip"
                    [class.v2-chip-warn]="reconResult.summary.unmatchedStripe">Unmatched Stripe tx: {{ reconResult.summary.unmatchedStripe }}</span>
              <span class="v2-chip v2-chip-warn" *ngIf="reconResult.summary.noOrderId">No order-ID: {{ reconResult.summary.noOrderId }}</span>
            </div>
            <p class="v2-note"
               *ngIf="(reconResult.summary.unmatchedVipps && !reconResult.summary.settlementVipps) || (reconResult.summary.unmatchedStripe && !reconResult.summary.settlementStripe)">
              ⚠ Unmatched transactions exist for a provider whose settlement file wasn't uploaded. Upload both files and re-run.
            </p>

            <div class="v2-toolbar">
              <button mat-stroked-button (click)="exportRecon()" [disabled]="!reconResult.customers?.length">
                <mat-icon>download</mat-icon> Export CSV
              </button>
              <button mat-stroked-button (click)="exportReportXlsx()" [disabled]="!reconResult.customers?.length"
                      matTooltip="Full report: summary, customers, unmatched order numbers, unassigned groups">
                <mat-icon>table_view</mat-icon> Export report (Excel)
              </button>
              <button mat-stroked-button *ngIf="reconResult.flaggedTotal" (click)="showFlagged = !showFlagged">
                <mat-icon>flag</mat-icon> {{ showFlagged ? 'Hide' : 'Show' }} flagged ({{ reconResult.flaggedTotal }})
              </button>
              <button mat-stroked-button *ngIf="reconResult.unassignedBreakdown?.length" (click)="showUnassigned = !showUnassigned">
                <mat-icon>help_outline</mat-icon> {{ showUnassigned ? 'Hide' : 'Show' }} Unassigned breakdown ({{ reconResult.unassignedBreakdown.length }})
              </button>
              <span class="v2-spacer"></span>
              <button mat-flat-button color="accent" *ngIf="!viewingSaved" (click)="saveAvstemming()" [disabled]="savingAvstemming">
                <mat-icon>save</mat-icon> {{ savingAvstemming ? 'Saving…' : 'Save avstemming' }}
              </button>
              <button mat-stroked-button color="warn" *ngIf="viewingSaved" (click)="deleteAvstemming()"
                      [disabled]="deletingAvstemming"
                      matTooltip="Delete this saved month so it can be run again — needed after splitting a customer or moving a group">
                <mat-icon>delete_outline</mat-icon> {{ deletingAvstemming ? 'Deleting…' : 'Delete saved avstemming' }}
              </button>
            </div>
            <p class="v2-note" *ngIf="savedNote" [class.v2-saved-ok]="viewingSaved">{{ savedNote }}</p>

            <div class="v2-table-wrap" *ngIf="reconResult.customers?.length">
              <table class="v2-plain-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Customer</th>
                    <th class="num">Confirmed subsidy (ex-VAT)</th>
                    <th class="num">Credit consumed</th>
                    <th class="num" matTooltip="Bottle deposit paid with credit — the employer already fronted this at checkout, so it's billed back, VAT-free, as its own invoice line.">Pant (credit, billed)</th>
                    <th class="num">Billable total</th>
                    <th class="num" matTooltip="Bottle deposit paid by card/Vipps — reference only, never billed. The buyer paid it themselves and reclaims it via the bottle-return system.">Pant (card, ref)</th>
                    <th class="num">Flagged</th>
                  </tr>
                </thead>
                <tbody>
                  <ng-container *ngFor="let row of reconResult.customers">
                    <tr>
                      <td>
                        <button mat-icon-button *ngIf="row.groups?.length > 1" (click)="expandedCustomer[row.customerId] = !expandedCustomer[row.customerId]"
                                matTooltip="Group-by-group breakdown">
                          <mat-icon>{{ expandedCustomer[row.customerId] ? 'expand_less' : 'expand_more' }}</mat-icon>
                        </button>
                      </td>
                      <td>{{ row.customerId === 'UNASSIGNED' ? 'Unassigned' : customerName(row.customerId) }}</td>
                      <td class="num">{{ row.confirmedSubsidyExVAT | number:'1.2-2' }}</td>
                      <td class="num">{{ row.creditConsumed | number:'1.2-2' }}</td>
                      <td class="num">{{ row.creditPantTotal | number:'1.2-2' }}</td>
                      <td class="num"><strong>{{ row.billableTotal | number:'1.2-2' }}</strong></td>
                      <td class="num v2-ref-cell">{{ (row.pantTotal - row.creditPantTotal) | number:'1.2-2' }}</td>
                      <td class="num" [class.v2-flag-cell]="row.flaggedCount">{{ row.flaggedCount || '—' }}</td>
                    </tr>
                    <tr *ngIf="expandedCustomer[row.customerId] && row.groups?.length" class="v2-group-breakdown">
                      <td></td>
                      <td colspan="5">
                        <table class="v2-plain-table v2-nested-table">
                          <thead><tr><th>Group</th><th class="num">Subsidy (ex-VAT)</th><th class="num">Credit</th><th class="num">Pant (credit)</th><th class="num">Total</th><th class="num">Pant (card, ref)</th></tr></thead>
                          <tbody>
                            <tr *ngFor="let g of row.groups">
                              <td>{{ g.groupName || (g.groupId ? '(unnamed group)' : 'No group') }}</td>
                              <td class="num">{{ g.confirmedSubsidyExVAT | number:'1.2-2' }}</td>
                              <td class="num">{{ g.creditConsumed | number:'1.2-2' }}</td>
                              <td class="num">{{ g.creditPantTotal | number:'1.2-2' }}</td>
                              <td class="num"><strong>{{ g.billableTotal | number:'1.2-2' }}</strong></td>
                              <td class="num v2-ref-cell">{{ (g.pantTotal - g.creditPantTotal) | number:'1.2-2' }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </ng-container>
                  <tr class="v2-invoice-total">
                    <td></td>
                    <td>Total</td>
                    <td class="num">{{ reconResult.grandSubsidy | number:'1.2-2' }}</td>
                    <td class="num">{{ reconResult.grandCredit | number:'1.2-2' }}</td>
                    <td class="num">{{ reconResult.grandCreditPant | number:'1.2-2' }}</td>
                    <td class="num"><strong>{{ reconResult.grandTotal | number:'1.2-2' }}</strong></td>
                    <td class="num v2-ref-cell">{{ (reconResult.grandPant - reconResult.grandCreditPant) | number:'1.2-2' }}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="v2-note" *ngIf="reconResult.customers?.length">
              <em>Pant (credit, billed)</em> is bottle deposit the employer already fronted via credit — it's part of Billable total and gets its own
              VAT-free line on the invoice. <em>Pant (card, ref)</em> is deposit the buyer paid themselves (card/Vipps) — reference only, never billed;
              they reclaim it via the bottle-return system.
            </p>
            <div class="v2-empty" *ngIf="!reconResult.customers?.length">
              <mat-icon>inbox</mat-icon> No billable amounts matched for this month.
            </div>

            <!-- Unassigned breakdown by group or system (what needs to be linked) -->
            <div class="v2-table-wrap" style="margin-top: 16px;" *ngIf="showUnassigned && reconResult.unassignedBreakdown?.length">
              <p class="v2-note">
                Groups and systems behind the <strong>Unassigned</strong> total, with what needs to happen before their amount lands on a real invoice.
                Groups are linked in <strong>Customer Mapping</strong>; systems are linked via the kundekort's <strong>Systemer</strong> tab. Re-run after linking.
              </p>
              <table class="v2-plain-table">
                <thead><tr><th>Group / System</th><th>Needs</th><th>ID</th><th class="num">Transactions</th><th class="num">Billable (ex-VAT)</th><th class="num">Flagged</th></tr></thead>
                <tbody>
                  <tr *ngFor="let u of reconResult.unassignedBreakdown">
                    <td>{{ u.groupName || '(no group, no system — guest with nothing to link)' }}</td>
                    <td>
                      <span class="v2-chip" [ngClass]="{
                        'v2-chip-warn': u.reason === 'system-unlinked',
                        'v2-chip-ok': u.reason === 'group-unlinked'
                      }">{{
                        u.reason === 'system-unlinked' ? 'Link system → customer' :
                        u.reason === 'system-shared' ? 'Split shared system' :
                        u.reason === 'group-unlinked' ? 'Link group → customer' : '—'
                      }}</span>
                    </td>
                    <td>{{ u.groupId || u.systemId || '—' }}</td>
                    <td class="num">{{ u.count }}</td>
                    <td class="num">{{ u.billable | number:'1.2-2' }}</td>
                    <td class="num" [class.v2-flag-cell]="u.flaggedCount">{{ u.flaggedCount || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- flagged transactions (unmatched / refunded) -->
            <div class="v2-table-wrap" style="margin-top: 16px;" *ngIf="showFlagged && reconResult.flagged?.length">
              <p class="v2-note" *ngIf="reconResult.flaggedTruncated">
                Showing the first {{ reconResult.flagged.length }} of {{ reconResult.flaggedTotal }} flagged transactions.
              </p>
              <table class="v2-plain-table">
                <thead><tr><th>Order ID</th><th>Customer</th><th>Method</th><th>Reason</th><th class="num">Subsidy (ex-VAT)</th></tr></thead>
                <tbody>
                  <tr *ngFor="let f of reconResult.flagged">
                    <td>{{ f.orderId || '—' }}</td>
                    <td>{{ f.customerId === 'UNASSIGNED' ? 'Unassigned' : customerName(f.customerId) }}</td>
                    <td>{{ f.payType }}</td>
                    <td><span class="v2-reason v2-reason-{{ f.reason }}">{{ f.reason }}</span></td>
                    <td class="num">{{ f.billableSubsidyExVAT | number:'1.2-2' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-container>

          <div class="v2-empty" *ngIf="!reconResult">
            <mat-icon>fact_check</mat-icon> Upload settlement files above and run reconciliation to see billable amounts.
          </div>
        </div>
      </mat-tab>

      <!-- Møtemat ---------------------------------------------------------- -->
      <mat-tab>
        <ng-template mat-tab-label>
          <span class="v2-tab-label"><mat-icon>restaurant</mat-icon> Møtemat</span>
        </ng-template>

        <div class="v2-tab-panel">
          <p class="v2-note">
            Meeting food customers ordered in the portal and Frigg approved for {{ invoiceMonth }}, at the
            product prices they were quoted. Each order becomes its own invoice line in "Generate invoices" above.
            Pending, rejected and cancelled orders are not billed.
          </p>
          <mat-spinner *ngIf="motematLoading" diameter="24" class="v2-inline-spin"></mat-spinner>
          <div class="v2-empty" *ngIf="!motematLoading && !motematRows.length">
            <mat-icon>restaurant</mat-icon> No approved møtemat orders this month.
          </div>
          <div class="v2-table-wrap" *ngIf="!motematLoading && motematRows.length">
            <table class="v2-plain-table">
              <thead><tr><th>Customer</th><th class="num">Orders</th><th class="num">Total incl. VAT</th></tr></thead>
              <tbody>
                <tr *ngFor="let r of motematRows">
                  <td>{{ r.name }}</td>
                  <td class="num">{{ r.orderCount }}</td>
                  <td class="num">{{ r.grossIncVat | number:'1.2-2' }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="v2-invoice-total"><td>Sum</td><td class="num">—</td><td class="num">{{ motematTotal | number:'1.2-2' }}</td></tr>
              </tfoot>
            </table>
          </div>
        </div>
      </mat-tab>

      <!-- Garantiomsetning --------------------------------------------------- -->
      <mat-tab>
        <ng-template mat-tab-label>
          <span class="v2-tab-label"
                [matBadge]="garantiomsetningCmp?.unlinkedCount" matBadgeSize="small" matBadgeOverlap="false"
                [matBadgeHidden]="!garantiomsetningCmp?.unlinkedCount">
            <mat-icon>analytics</mat-icon> Garantiomsetning
          </span>
        </ng-template>

        <div class="v2-tab-panel">
          <p class="v2-note">
            Any billable shortfall shown below for {{ invoiceMonth }} is folded into "Generate invoices" above automatically.
            Frigg-app turnover isn't computed automatically on page load (that used to make this section spin) — it's
            recomputed for any row missing it whenever you click "Run reconciliation" on the Reconcile tab, or use
            "Recompute missing" / a row's refresh icon here.
          </p>
          <garantiomsetning [month]="invoiceMonth"></garantiomsetning>
        </div>
      </mat-tab>

      <!-- Driftskostnader (operating cost) ------------------------------------ -->
      <mat-tab>
        <ng-template mat-tab-label>
          <span class="v2-tab-label"><mat-icon>build</mat-icon> Driftskostnader</span>
        </ng-template>

        <div class="v2-tab-panel">
          <p class="v2-note">
            {{ DRIFTKOSTNAD_RATE_EX_VAT }} kr ex-VAT per system per month, charged automatically to every customer with linked systems —
            each customer can opt out via the <strong>Driftskostnader</strong> toggle on their kundekort (Operations tab). Folded into
            "Generate invoices" above automatically. System ↔ customer linkage is the same one Garantiomsetning uses (Customer Mapping /
            the kundekort's Systemer tab) — a system with no link isn't billed here either.
          </p>
          <mat-spinner *ngIf="driftLoading" diameter="24" class="v2-inline-spin"></mat-spinner>
          <div class="v2-empty" *ngIf="!driftLoading && !driftRows.length">
            <mat-icon>build</mat-icon> No customers with linked, active systems.
          </div>
          <div class="v2-table-wrap" *ngIf="!driftLoading && driftRows.length">
            <table class="v2-plain-table">
              <thead><tr><th>Customer</th><th class="num">Systems</th><th class="num">Total ex-VAT</th></tr></thead>
              <tbody>
                <tr *ngFor="let r of driftRows">
                  <td>{{ r.name }}</td>
                  <td class="num">{{ r.systemCount }}</td>
                  <td class="num">{{ r.billable | number:'1.2-2' }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="v2-invoice-total"><td>Sum</td><td class="num">—</td><td class="num">{{ driftTotal | number:'1.2-2' }}</td></tr>
              </tfoot>
            </table>
          </div>
        </div>
      </mat-tab>

      <!-- Avtalelinjer (recurring per-system contract charges) ----------------- -->
      <mat-tab>
        <ng-template mat-tab-label>
          <span class="v2-tab-label"><mat-icon>receipt_long</mat-icon> Avtaler</span>
        </ng-template>

        <div class="v2-tab-panel">
          <p class="v2-note">
            Recurring charges per <strong>system</strong> — rent, driftstøtte, service. These replace the invoices issued by hand in
            PowerOffice Go (e.g. <em>Leie friggkantine per måned</em>). All are billed at <strong>25% MVA</strong>, not the food rate, and go
            to the customer linked to that system. Amounts are per system, so a customer with two sites can have two different rents on two
            different invoices. Folded into "Generate invoices" automatically for the selected month.
            The {{ DRIFTKOSTNAD_RATE_EX_VAT }} kr administrasjonsgebyr is <em>not</em> set here — it is charged automatically (Driftskostnader tab).
          </p>

          <div class="v2-error" *ngIf="avtaleError">{{ avtaleError }}</div>

          <div class="v2-form-row">
            <mat-form-field appearance="outline">
              <mat-label>System</mat-label>
              <mat-select [(ngModel)]="newAvtale.systemId">
                <mat-option *ngFor="let sy of avtaleSystems" [value]="sy.systemId">{{ sy.name }} — {{ sy.owner }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Type</mat-label>
              <mat-select [(ngModel)]="newAvtale.kind" (selectionChange)="onAvtaleKindChange()">
                <mat-option *ngFor="let k of AVTALE_KINDS" [value]="k.kind">{{ k.label }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="wide">
              <mat-label>Description (shown on the invoice)</mat-label>
              <input matInput [(ngModel)]="newAvtale.description" placeholder="Leie friggkantine per måned">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Amount ex-VAT</mat-label>
              <input matInput type="number" [(ngModel)]="newAvtale.amountExVat" placeholder="10000">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Interval</mat-label>
              <mat-select [(ngModel)]="newAvtale.interval">
                <mat-option value="monthly">Monthly</mat-option>
                <mat-option value="quarterly">Quarterly</mat-option>
                <mat-option value="yearly">Yearly</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>From (YYYY-MM)</mat-label>
              <input matInput [(ngModel)]="newAvtale.startMonth" placeholder="2026-09">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>To (optional)</mat-label>
              <input matInput [(ngModel)]="newAvtale.endMonth" placeholder="blank = ongoing">
            </mat-form-field>
            <button mat-flat-button color="primary" (click)="addAvtale()" [disabled]="avtaleSaving['new']">
              <mat-icon>add</mat-icon> Add
            </button>
          </div>

          <div class="v2-empty" *ngIf="!avtaleRows.length">
            <mat-icon>receipt_long</mat-icon> No agreements yet. Add the rent and driftstøtte for each system above.
          </div>

          <div class="v2-table-wrap" *ngIf="avtaleRows.length">
            <table class="v2-plain-table">
              <thead>
                <tr>
                  <th>System</th><th>Customer</th><th>Type</th><th>Description</th>
                  <th class="num">Amount ex-VAT</th><th>Interval</th><th>Period</th>
                  <th>{{ invoiceMonth }}</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let r of avtaleRows" [class.v2-row-off]="!r.active">
                  <td>{{ r.systemName }}</td>
                  <td>{{ r.owner }}</td>
                  <td>{{ r.kind }}</td>
                  <td>{{ r.description }}</td>
                  <td class="num">{{ r.amountExVat | number:'1.2-2' }}</td>
                  <td>{{ r.interval }}</td>
                  <td>{{ r.startMonth }} – {{ r.endMonth || '…' }}</td>
                  <td>
                    <span *ngIf="r.due" class="v2-pill-ok">billed</span>
                    <span *ngIf="!r.due" class="v2-pill-muted">—</span>
                  </td>
                  <td class="num">
                    <button mat-icon-button (click)="toggleAvtale(r)" [disabled]="avtaleSaving[r.dbid]"
                            [matTooltip]="r.active ? 'Pause this agreement' : 'Reactivate'">
                      <mat-icon>{{ r.active ? 'pause_circle' : 'play_circle' }}</mat-icon>
                    </button>
                    <button mat-icon-button color="warn" (click)="removeAvtale(r)" [disabled]="avtaleSaving[r.dbid]" matTooltip="Delete">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="v2-invoice-total">
                  <td colspan="4">Sum billed for {{ invoiceMonth }}</td>
                  <td class="num">{{ avtaleTotal | number:'1.2-2' }}</td>
                  <td colspan="4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </mat-tab>

      <!-- Juice (manual, per system per month) -------------------------------- -->
      <mat-tab>
        <ng-template mat-tab-label>
          <span class="v2-tab-label"><mat-icon>local_drink</mat-icon> Juice</span>
        </ng-template>

        <div class="v2-tab-panel">
          <p class="v2-note">
            Juice for <strong>{{ invoiceMonth }}</strong>, entered by hand — the BIB dispensers report nothing (coffee comes from Vendon
            automatically). Pick the system, the juice type and how many BIBs were used; cups are calculated
            (Appelsin 120, Eple 145, Iste fersken 250 per BIB) or can be typed directly. Billed at the customer's food rate (15%)
            to the customer linked to that system, one line per system on "Generate invoices".
          </p>

          <div class="v2-error" *ngIf="juiceError">{{ juiceError }}</div>

          <div class="v2-form-row">
            <mat-form-field appearance="outline">
              <mat-label>System</mat-label>
              <mat-select [(ngModel)]="newJuice.systemId">
                <mat-option *ngFor="let sy of avtaleSystems" [value]="sy.systemId">{{ sy.name }} — {{ sy.owner }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Juice</mat-label>
              <mat-select [(ngModel)]="newJuice.juiceType" (selectionChange)="onJuiceBibsChange()">
                <mat-option *ngFor="let t of JUICE_TYPES" [value]="t.key">{{ t.label }}{{ t.cupsPerBib ? ' (' + t.cupsPerBib + ' cups/BIB)' : '' }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" *ngIf="newJuice.juiceType !== 'cups'">
              <mat-label>BIBs used</mat-label>
              <input matInput type="number" min="0" step="0.5" [(ngModel)]="newJuice.bibs" (ngModelChange)="onJuiceBibsChange()" placeholder="1">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Cups</mat-label>
              <input matInput type="number" min="0" step="1" [(ngModel)]="newJuice.cups" placeholder="120">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Price per cup ex-VAT</mat-label>
              <input matInput type="number" min="0" step="0.5" [(ngModel)]="newJuice.pricePerCupExVat">
            </mat-form-field>
            <mat-form-field appearance="outline" class="wide">
              <mat-label>Note (optional)</mat-label>
              <input matInput [(ngModel)]="newJuice.note" placeholder="e.g. counted 31.08">
            </mat-form-field>
            <button mat-flat-button color="primary" (click)="addJuice()" [disabled]="juiceSaving">
              <mat-icon>add</mat-icon> Add
            </button>
          </div>

          <div class="v2-empty" *ngIf="!juiceRows.length">
            <mat-icon>local_drink</mat-icon> No juice entered for {{ invoiceMonth }}.
          </div>

          <div class="v2-table-wrap" *ngIf="juiceRows.length">
            <table class="v2-plain-table">
              <thead>
                <tr>
                  <th>System</th><th>Customer</th><th>Juice</th><th class="num">BIBs</th><th class="num">Cups</th>
                  <th class="num">Price/cup</th><th class="num">Amount ex-VAT</th><th>Note</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let r of juiceRows">
                  <td>{{ r.systemName }}</td>
                  <td>{{ r.owner }}</td>
                  <td>{{ r.typeLabel }}</td>
                  <td class="num">{{ r.bibs ?? '—' }}</td>
                  <td class="num">{{ r.cups }}</td>
                  <td class="num">{{ r.pricePerCupExVat | number:'1.2-2' }}</td>
                  <td class="num">{{ r.amountExVat | number:'1.2-2' }}</td>
                  <td>{{ r.note }}</td>
                  <td class="num">
                    <button mat-icon-button color="warn" (click)="removeJuice(r)" matTooltip="Delete">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="v2-invoice-total">
                  <td colspan="6">Juice billed for {{ invoiceMonth }}</td>
                  <td class="num">{{ juiceTotal | number:'1.2-2' }}</td>
                  <td colspan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </mat-tab>

      <!-- Coffee (Vendon machines: who pays, price, which cups) ---------------- -->
      <mat-tab>
        <ng-template mat-tab-label>
          <span class="v2-tab-label"><mat-icon>coffee</mat-icon> Coffee</span>
        </ng-template>
        <div class="v2-tab-panel">
          <coffee-machines [month]="invoiceMonth"></coffee-machines>
        </div>
      </mat-tab>

      <!-- History ------------------------------------------------------------ -->
      <mat-tab>
        <ng-template mat-tab-label>
          <span class="v2-tab-label"><mat-icon>history</mat-icon> History</span>
        </ng-template>

        <div class="v2-tab-panel">
          <p class="v2-note">Saved monthly reconciliations. Open one to view its result, re-generate invoices, or download the archived settlement files.</p>
          <div class="v2-table-wrap" *ngIf="avstemminger.length; else noAvstemming">
            <table class="v2-plain-table">
              <thead><tr><th>Month</th><th>Status</th><th class="num">Subsidy</th><th class="num">Credit</th><th class="num">Total</th><th>Files</th><th>Saved by</th><th></th></tr></thead>
              <tbody>
                <tr *ngFor="let a of avstemminger">
                  <td><strong>{{ a.period?.month }}</strong></td>
                  <td><span class="v2-chip">{{ a.status }}</span></td>
                  <td class="num">{{ a.grandSubsidy | number:'1.2-2' }}</td>
                  <td class="num">{{ a.grandCredit | number:'1.2-2' }}</td>
                  <td class="num"><strong>{{ a.grandTotal | number:'1.2-2' }}</strong></td>
                  <td class="v2-av-files">
                    <a *ngIf="a.settlementMeta?.vipps?.url" [href]="a.settlementMeta.vipps.url" target="_blank" matTooltip="Vipps file">Vipps</a>
                    <a *ngIf="a.settlementMeta?.stripe?.url" [href]="a.settlementMeta.stripe.url" target="_blank" matTooltip="Stripe file">Stripe</a>
                    <span *ngIf="!a.settlementMeta?.vipps?.url && !a.settlementMeta?.stripe?.url">—</span>
                  </td>
                  <td class="v2-av-by">{{ a.createdBy || '—' }}</td>
                  <td><button mat-stroked-button (click)="openAvstemming(a)"><mat-icon>visibility</mat-icon> Open</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <ng-template #noAvstemming>
            <div class="v2-empty"><mat-icon>history</mat-icon> No saved avstemminger yet — run a reconciliation and click "Save avstemming".</div>
          </ng-template>
        </div>
      </mat-tab>

    </mat-tab-group>
  </div>
</div>
